import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';

import { PlayersLimitReachedNotification } from '../../../../../../shared/application/components/PlayersLimitReachedNotification/PlayersLimitReachedNotification';
import { ReactionsOverlay } from '../../../../../../shared/application/components/ReactionsOverlay/ReactionsOverlay';
import { useHostClientContext } from '../HostClientProvider/hooks/useHostClientContext';
import { AnalyticsResultsSidebar } from './components/AnalyticsResultsSidebar/AnalyticsResultsSidebar';
import { GameControlHeader } from './components/GameControlHeader/GameControlHeader';
import { HandQueueSidebar } from './components/HandQueueSidebar/HandQueueSidebar';
import { HostGeniallyView } from './components/HostGeniallyView/HostGeniallyView';
import { HostPlaySidebarSummary } from './components/HostPlaySidebarSummary/HostPlaySidebarSummary';
import { InviteSidebar } from './components/InviteSidebar/InviteSidebar';
import { ParticipantsSidebar } from './components/ParticipantsSidebar/ParticipantsSidebar';
import { SettingsSidebar } from './components/SettingsSidebar/SettingsSidebar';
import { HostGameProvider, useHostGameContext } from './HostGame.context';
import {
  BannerWrapper,
  ContentWrapper,
  GameControlHeaderWrapper,
  MainColumn,
  PlaySidebarSummaryWrapper,
  Wrapper,
} from './HostGame.styled';
import { useOpenSessionConfigModal } from './hooks/useOpenSessionConfigModal';

const HostGameContent = observer(() => {
  const {
    settingsSidebar,
    analyticsSidebar,
    participantsSidebar,
    handQueueSidebar,
    inviteSidebar,
    reactions,
    isOpenMode,
    shouldAutoOpenSessionConfigModal,
    initialSessionConfigModal,
  } = useHostGameContext();

  const { client } = useHostClientContext();

  const [isPlayersLimitBannerDismissed, setIsPlayersLimitBannerDismissed] =
    useState(false);

  // Re-arm the banner for the next time the limit is hit, otherwise a dismissal
  // from an earlier limit-reached moment would silently suppress all future ones.
  useEffect(() => {
    if (client.isPlayersLimitReached) {
      setIsPlayersLimitBannerDismissed(false);
    }
  }, [client.isPlayersLimitReached]);

  const showPlayersLimitBanner =
    client.isPlayersLimitReached && !isPlayersLimitBannerDismissed;

  const { openSessionConfigModal } = useOpenSessionConfigModal({
    isInitialConfig: true,
    onClose: () => {
      initialSessionConfigModal.close();

      if (isOpenMode) {
        inviteSidebar.open();
      }
    },
  });

  // Runs once on mount, same as the beforeunload effect below — shouldAutoOpenSessionConfigModal
  // is fixed for the lifetime of this session (new session + feature flag), so there's nothing
  // to react to afterwards.
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount, see comment above
  useEffect(() => {
    if (shouldAutoOpenSessionConfigModal) {
      openSessionConfigModal();
    }
  }, []);

  const handleCloseSettingsSidebarClick = () => {
    settingsSidebar.close();
  };

  const handleCloseParticipantsSidebarClick = () => {
    participantsSidebar.close();
  };

  const handleCloseHandQueueSidebarClick = () => {
    handQueueSidebar.close();
  };

  const handleCloseInviteSidebarClick = () => {
    inviteSidebar.close();
  };

  const handleCloseAnalyticsSidebarClick = () => {
    analyticsSidebar.close();
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  return (
    <Wrapper>
      {isOpenMode && (
        <InviteSidebar
          isOpen={inviteSidebar.isOpen}
          onClose={handleCloseInviteSidebarClick}
        />
      )}

      <MainColumn>
        <ReactionsOverlay reactions={reactions} />

        <GameControlHeaderWrapper>
          <GameControlHeader />
        </GameControlHeaderWrapper>

        <BannerWrapper $show={showPlayersLimitBanner}>
          <PlayersLimitReachedNotification
            show={showPlayersLimitBanner}
            onClose={() => setIsPlayersLimitBannerDismissed(true)}
          />
        </BannerWrapper>

        <SettingsSidebar
          isOpen={settingsSidebar.isOpen}
          onClose={handleCloseSettingsSidebarClick}
        />

        <ParticipantsSidebar
          isOpen={participantsSidebar.isOpen}
          onClose={handleCloseParticipantsSidebarClick}
        />

        <HandQueueSidebar
          isOpen={handQueueSidebar.isOpen}
          onClose={handleCloseHandQueueSidebarClick}
        />

        <ContentWrapper>
          <HostGeniallyView showGeniallyAsFullHeight />

          <PlaySidebarSummaryWrapper>
            <HostPlaySidebarSummary />
          </PlaySidebarSummaryWrapper>

          <AnalyticsResultsSidebar
            isOpen={analyticsSidebar.isOpen}
            selectedQuestionId={analyticsSidebar.selectedQuestionId}
            onClose={handleCloseAnalyticsSidebarClick}
          />
        </ContentWrapper>
      </MainColumn>
    </Wrapper>
  );
});

export const HostGame = observer(() => {
  return (
    <HostGameProvider>
      <HostGameContent />
    </HostGameProvider>
  );
});
